import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Modal, ConfirmModal } from '../../components/ui/Modal';
import {
  listCustomers,
  createCustomer,
  updateCustomer,
  deleteCustomer,
  createVehicle,
  deleteVehicle,
  type CustomerWithVehicles,
} from '../../api/customers';
import { ApiError } from '../../api/client';
import { Search, Plus, Pencil, Trash2, Car, User } from 'lucide-react';

const emptyCustomerForm = { name: '', phone: '', remark: '' };
const emptyVehicleForm = { plate: '', brand: '', model: '' };

export function CustomersPage(): React.JSX.Element {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<CustomerWithVehicles | null>(null);
  const [form, setForm] = useState(emptyCustomerForm);
  const [deleting, setDeleting] = useState<CustomerWithVehicles | null>(null);
  const [vehicleFor, setVehicleFor] = useState<CustomerWithVehicles | null>(null);
  const [vehicleForm, setVehicleForm] = useState(emptyVehicleForm);
  const [deletingVehicle, setDeletingVehicle] = useState<{ customer: CustomerWithVehicles; vehicleId: string; plate: string } | null>(null);
  const [error, setError] = useState('');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['customers', search],
    queryFn: () => listCustomers(search || undefined),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['customers'] });

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim() || undefined,
        remark: form.remark.trim() || undefined,
      };
      return editing ? updateCustomer(editing.id, payload) : createCustomer(payload);
    },
    onSuccess: () => {
      setFormOpen(false);
      setEditing(null);
      setForm(emptyCustomerForm);
      setError('');
      invalidate();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : '保存失败'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteCustomer(id),
    onSuccess: () => {
      setDeleting(null);
      invalidate();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : '删除失败'),
  });

  const vehicleMutation = useMutation({
    mutationFn: () =>
      createVehicle({
        customerId: vehicleFor!.id,
        plate: vehicleForm.plate.trim(),
        brand: vehicleForm.brand.trim() || undefined,
        model: vehicleForm.model.trim() || undefined,
      }),
    onSuccess: () => {
      setVehicleFor(null);
      setVehicleForm(emptyVehicleForm);
      setError('');
      invalidate();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : '添加车辆失败'),
  });

  const deleteVehicleMutation = useMutation({
    mutationFn: (vehicleId: string) => deleteVehicle(vehicleId),
    onSuccess: () => {
      setDeletingVehicle(null);
      invalidate();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : '删除车辆失败'),
  });

  const openCreate = () => {
    setEditing(null);
    setForm(emptyCustomerForm);
    setError('');
    setFormOpen(true);
  };

  const openEdit = (c: CustomerWithVehicles) => {
    setEditing(c);
    setForm({ name: c.name, phone: c.phone ?? '', remark: c.remark ?? '' });
    setError('');
    setFormOpen(true);
  };

  return (
    <div>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#1E293B]">客户车辆管理</h1>
          <p className="mt-1 text-sm text-[#64748B]">管理门店客户档案与车辆信息</p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="mr-1 h-4 w-4" />
          新增客户
        </Button>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-[#FEF2F2] px-4 py-3 text-sm text-[#DC2626]">{error}</div>
      )}

      <Card className="mb-4">
        <CardContent className="flex flex-wrap items-center gap-4">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#94A3B8]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="搜索客户姓名或手机号..."
              className="pl-9"
            />
          </div>
          <span className="text-sm text-[#64748B]">
            共 <span className="font-medium text-[#1E293B]">{data?.length ?? 0}</span> 位客户
          </span>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>客户列表</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-12 text-center text-sm text-[#94A3B8]">加载中...</div>
          ) : isError ? (
            <div className="py-12 text-center text-sm text-[#DC2626]">加载失败，请刷新重试</div>
          ) : !data?.length ? (
            <div className="py-12 text-center">
              <User className="mx-auto h-12 w-12 text-[#E2E8F0]" />
              <p className="mt-3 text-sm text-[#94A3B8]">
                {search ? '未找到匹配的客户' : '暂无客户，点击右上角"新增客户"'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E2E8F0] text-left text-sm text-[#64748B]">
                    <th className="pb-3 font-medium">姓名</th>
                    <th className="pb-3 font-medium">手机号</th>
                    <th className="pb-3 font-medium">车辆</th>
                    <th className="pb-3 font-medium">备注</th>
                    <th className="pb-3 font-medium text-right">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F1F5F9]">
                  {data.map((customer) => (
                    <tr key={customer.id} className="text-sm">
                      <td className="py-3 font-medium text-[#1E293B]">{customer.name}</td>
                      <td className="py-3 text-[#64748B]">{customer.phone || '-'}</td>
                      <td className="py-3">
                        <div className="flex flex-wrap items-center gap-1.5">
                          {customer.vehicles.map((v) => (
                            <span
                              key={v.id}
                              className="group inline-flex items-center gap-1 rounded-md bg-[#F1F5F9] px-2 py-0.5 text-xs text-[#334155]"
                            >
                              <Car className="h-3 w-3" />
                              {v.plate}
                              <button
                                title="删除车辆"
                                onClick={() => setDeletingVehicle({ customer, vehicleId: v.id, plate: v.plate })}
                                className="text-[#94A3B8] hover:text-[#DC2626]"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                          <button
                            onClick={() => {
                              setVehicleFor(customer);
                              setVehicleForm(emptyVehicleForm);
                            }}
                            className="rounded-md border border-dashed border-[#CBD5E1] px-2 py-0.5 text-xs text-[#64748B] hover:border-[#2563EB] hover:text-[#2563EB]"
                          >
                            + 加车
                          </button>
                        </div>
                      </td>
                      <td className="py-3 text-[#64748B]">{customer.remark || '-'}</td>
                      <td className="py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            title="编辑"
                            onClick={() => openEdit(customer)}
                            className="rounded-lg p-1.5 text-[#2563EB] hover:bg-[#EFF6FF]"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            title="删除"
                            onClick={() => setDeleting(customer)}
                            className="rounded-lg p-1.5 text-[#DC2626] hover:bg-[#FEF2F2]"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 新增/编辑客户 */}
      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={editing ? '编辑客户' : '新增客户'}
        footer={
          <>
            <Button variant="outline" onClick={() => setFormOpen(false)} disabled={saveMutation.isPending}>
              取消
            </Button>
            <Button
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending || !form.name.trim()}
            >
              {saveMutation.isPending ? '保存中...' : '保存'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-[#334155]">姓名 *</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-[#334155]">手机号</label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-[#334155]">备注</label>
            <Input value={form.remark} onChange={(e) => setForm({ ...form, remark: e.target.value })} />
          </div>
        </div>
      </Modal>

      {/* 添加车辆 */}
      <Modal
        open={!!vehicleFor}
        onClose={() => setVehicleFor(null)}
        title={`添加车辆 — ${vehicleFor?.name ?? ''}`}
        footer={
          <>
            <Button variant="outline" onClick={() => setVehicleFor(null)} disabled={vehicleMutation.isPending}>
              取消
            </Button>
            <Button
              onClick={() => vehicleMutation.mutate()}
              disabled={vehicleMutation.isPending || !vehicleForm.plate.trim()}
            >
              {vehicleMutation.isPending ? '添加中...' : '添加'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-[#334155]">车牌号 *</label>
            <Input
              value={vehicleForm.plate}
              onChange={(e) => setVehicleForm({ ...vehicleForm, plate: e.target.value })}
              placeholder="如：粤B12345"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-[#334155]">品牌</label>
              <Input
                value={vehicleForm.brand}
                onChange={(e) => setVehicleForm({ ...vehicleForm, brand: e.target.value })}
                placeholder="如：丰田"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-[#334155]">型号</label>
              <Input
                value={vehicleForm.model}
                onChange={(e) => setVehicleForm({ ...vehicleForm, model: e.target.value })}
                placeholder="如：卡罗拉"
              />
            </div>
          </div>
        </div>
      </Modal>

      {/* 删除客户确认 */}
      <ConfirmModal
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && deleteMutation.mutate(deleting.id)}
        title="删除客户"
        description={`确定删除客户「${deleting?.name}」吗？关联的 ${deleting?.vehicles.length ?? 0} 辆车也会一并删除。`}
        confirmText="删除"
        danger
        loading={deleteMutation.isPending}
      />

      {/* 删除车辆确认 */}
      <ConfirmModal
        open={!!deletingVehicle}
        onClose={() => setDeletingVehicle(null)}
        onConfirm={() => deletingVehicle && deleteVehicleMutation.mutate(deletingVehicle.vehicleId)}
        title="删除车辆"
        description={`确定删除车辆「${deletingVehicle?.plate}」吗？`}
        confirmText="删除"
        danger
        loading={deleteVehicleMutation.isPending}
      />
    </div>
  );
}
